import { AccountTree, Chat, Memory, Router } from '@mui/icons-material';
import { Box, Chip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { CollapsibleSection } from './CollapsibleSection';
import type { ExecutionRun } from './executionRunGroup.types';
import { ToolCallDisplay } from './ToolCallDisplay';

interface ExecutionRunCardProps {
  run: ExecutionRun;
}

const runTypeConfig = {
  NARRATOR: { icon: <Chat fontSize="small" />, color: 'primary' as const, label: 'Narrator' },
  ORCHESTRATOR: { icon: <Router fontSize="small" />, color: 'secondary' as const, label: 'Orchestrator' },
  AGENT: { icon: <Memory fontSize="small" />, color: 'success' as const, label: 'Agent' },
  PLANNER: { icon: <AccountTree fontSize="small" />, color: 'warning' as const, label: 'Planner' },
};

type DisplayItem =
  | { kind: 'text'; content: string }
  | { kind: 'tool_call'; toolName: string; toolCallId: string; input: unknown }
  | { kind: 'tool_result'; toolName: string; toolCallId: string; output: unknown }
  | { kind: 'summary'; content: string };

export function ExecutionRunCard({ run }: ExecutionRunCardProps) {
  const config = runTypeConfig[run.runType as keyof typeof runTypeConfig] || runTypeConfig.NARRATOR;
  const [focusedToolCallId, setFocusedToolCallId] = useState<string | null>(null);

  const handleJumpToResult = useCallback((toolCallId: string) => {
    const resultElement = document.getElementById(`tool-result-${toolCallId}`);
    if (resultElement) {
      resultElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFocusedToolCallId(toolCallId);
      // Clear focus after animation completes
      setTimeout(() => setFocusedToolCallId(null), 2000);
    }
  }, []);

  // Build display items in message order, preserving the sequence
  const displayItems: DisplayItem[] = [];

  for (const msg of run.messages) {
    const metadata = msg.metadata as Record<string, unknown> | null;
    const msgType = msg.type;

    // Add any text content first (reasoning/planning text)
    if (msg.content?.trim()) {
      if (msgType === 'AGENT_SUMMARY') {
        displayItems.push({ kind: 'summary', content: msg.content });
      } else if (msgType === 'TEXT' || (msgType === 'TOOL_CALL' && msg.content.trim())) {
        // TEXT messages, or TOOL_CALL messages that have accompanying text
        displayItems.push({ kind: 'text', content: msg.content });
      }
    }

    // Add tool calls from this message
    if (msgType === 'TOOL_CALL' && metadata?.toolCalls) {
      const calls = metadata.toolCalls as Array<{ toolName: string; toolCallId: string; input: unknown }>;
      for (const tc of calls) {
        displayItems.push({ kind: 'tool_call', toolName: tc.toolName, toolCallId: tc.toolCallId, input: tc.input });
      }
    }

    // Add tool results from this message
    if (msgType === 'TOOL_RESULT' && metadata?.toolResults) {
      const results = metadata.toolResults as Array<{ toolName: string; toolCallId: string; output: unknown }>;
      for (const tr of results) {
        displayItems.push({ kind: 'tool_result', toolName: tr.toolName, toolCallId: tr.toolCallId, output: tr.output });
      }
    }
  }

  const headerTitle = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
      {config.icon}
      <Chip size="small" label={config.label} color={config.color} />
      {run.agentType && <Chip size="small" label={run.agentType} variant="outlined" />}
      {run.triggerContext && <Chip size="small" label={run.triggerContext} variant="outlined" />}
      <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
        {run.inputTokens} in / {run.outputTokens} out
        {run.durationMs && ` - ${(run.durationMs / 1000).toFixed(1)}s`}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <CollapsibleSection title={headerTitle} defaultExpanded={false}>
        {/* System Prompt - always collapsible */}
        <CollapsibleSection
          title={
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              System Prompt ({run.systemPrompt.length} chars)
            </Typography>
          }
        >
          <Box
            sx={{
              p: 1,
              bgcolor: 'grey.50',
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '11px',
              maxHeight: '200px',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {run.systemPrompt}
          </Box>
        </CollapsibleSection>

        {/* Messages in order */}
        {displayItems.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {displayItems.map((item, idx) => {
              if (item.kind === 'text') {
                return (
                  <Box
                    key={idx}
                    sx={{
                      my: 0.5,
                      p: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 0.5,
                      fontSize: '12px',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {item.content}
                  </Box>
                );
              }
              if (item.kind === 'summary') {
                return (
                  <Box
                    key={idx}
                    sx={{
                      my: 0.5,
                      p: 1,
                      bgcolor: 'success.50',
                      borderRadius: 0.5,
                      fontSize: '12px',
                      whiteSpace: 'pre-wrap',
                      borderLeft: '3px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'success.dark' }}>
                      Summary
                    </Typography>
                    <Box>{item.content}</Box>
                  </Box>
                );
              }
              if (item.kind === 'tool_call') {
                return (
                  <ToolCallDisplay
                    key={idx}
                    type="call"
                    toolName={item.toolName}
                    toolCallId={item.toolCallId}
                    data={item.input}
                    onJumpToResult={handleJumpToResult}
                  />
                );
              }
              if (item.kind === 'tool_result') {
                return (
                  <ToolCallDisplay
                    key={idx}
                    type="result"
                    toolName={item.toolName}
                    toolCallId={item.toolCallId}
                    data={item.output}
                    isFocused={focusedToolCallId === item.toolCallId}
                  />
                );
              }
              return null;
            })}
          </Box>
        )}
      </CollapsibleSection>
    </Box>
  );
}
